import { Box } from '@chakra-ui/react'
import {
  ContentNotFound,
  DetailPageNavigationHeader,
  ExhibitionAndEventAbout,
  ExhibitionAndEventDetailHeroCard,
  Hero,
  Layout,
  SsmFriendlyCardAdvantages,
  StickyHeader,
  ThreeDTourEmbedVideo,
} from '@common'
import { Link as LinkIcon } from '@icon'
import { ISSRPageData } from '@interfaces'
import { useShare } from '@lib/hooks'
import { ExhibitionService } from '@services'
import { Link as SSMLink } from '@ui'
import { checkStickyNavItem, createNavLink, getStickyId } from '@utils'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { Fragment } from 'react'

import dynamic from 'next/dynamic'

const SelectionsFromWork = dynamic(
  () => import('../../../../components/common/SelectionsFromWork')
)
const PhotoAndVideoSliderSection = dynamic(
  () => import('../../../../components/common/PhotoAndVideoSliderSection')
)
const SsmShopSliderSection = dynamic(
  () => import('../../../../components/common/SsmShopSliderSection')
)
const ExhibitionAndEventRelatedEvents = dynamic(
  () => import('../../../../components/common/ExhibitionAndEventRelatedEvents')
)

const ExhibitionDetailScreen = ({
  pageData,
  global: { navigation },
}: ISSRPageData) => {
  const { t } = useTranslation()
  const { asPath, locale, basePath } = useRouter()

  if (pageData?.error) {
    return (
      <Layout
        title={t('common:contentNotFound')}
        navigation={navigation}
        path={asPath}
      >
        <ContentNotFound />
      </Layout>
    )
  }

  // console.log(pageData.data.heroCard.bgImgUrl)
  //let webpimgUrl = pageData.data.heroCard.bgImgUrl.replace('files','files/styles/webp/public') + '.webp';
  return (
    <Layout
      title={pageData.data.heroCard.title}
      navigation={navigation}
      path={asPath}
    >
      <style jsx global>{`
        .css-5s4db3 div > div > a {
          color: #c71f3f;
        }
      `}</style>
      <Hero
        mobileBgImageUrl={pageData.data.heroCard.mobileBgImgUrl}
        bgImageUrl={pageData.data.heroCard.bgImgUrl}
        headerNav={
          <DetailPageNavigationHeader
            leftButtonLabel={t(
              'exhibition-and-event:eventAndExhibitionDetail.currentExhibitions'
            )}
            leftButtonHref={createNavLink('whatson', locale)}
            rightButtonLabel={t(
              'exhibition-and-event:eventAndExhibitionDetail.share'
            )}
            rightButtonAction={useShare}
            isRightButtonShareAction={true}
            shareProps={{
              title: `${pageData.data.heroCard.title} | SSM`,
              text: pageData.data.heroCard.title,
              url: basePath,
            }}
          />
        }
        isCoveredBg={false}
      ></Hero>
      <ExhibitionAndEventDetailHeroCard
        title={pageData.data.heroCard.title}
        date={pageData.data.heroCard.date}
        displayBuyTicketButtonGroup={true}
        displayFooterButtonGroup={false}
      />
      <Box as="main" pt={2}>
        <StickyHeader
          navs={pageData.data.stickyNavItems}
          containerProps={{
            zIndex: 6,
          }}
        >
          {pageData.data?.discoverLink &&
            pageData.data.discoverLink.map((link, index) => (
              <SSMLink
                key={index}
                display="flex"
                alignItems="center"
                fontSize="sm"
                fontWeight="light"
                color="black.primary3"
                _hover={{ cursor: 'pointer' }}
                position="relative"
                whiteSpace="pre"
                href={link.url}
                target="_blank"
              >
                {link.title}
                <LinkIcon ml="10px" width="15px" height="15px" />
              </SSMLink>
            ))}

          {pageData.data?.pressRelease && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={`${createNavLink('press', locale)}/${
                pageData.data?.pressRelease
              }`}
            >
              {t('nav:stickyItems.pressRelease')}
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}

          {pageData.data?.googleArtLink && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={pageData.data?.googleArtLink}
              target="_blank"
            >
              Google Arts & Culture
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}
          {pageData.data?.matterportLink && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={pageData.data?.matterportLink}
              target="_blank"
            >
              3D
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}
          {pageData.data?.ticketLink && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={pageData.data?.ticketLink}
              target="_blank"
            >
              Biletler
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}
        </StickyHeader>
        {checkStickyNavItem(
          pageData.data.stickyNavItems,
          'aboutTheExhibition'
        ) && (
          <ExhibitionAndEventAbout
            sectionId={getStickyId(
              pageData.data.stickyNavItems,
              'aboutTheExhibition'
            )}
            stickyTitle={pageData.data.eventAbout.stickyTitle}
            staticDescriptions={pageData.data.eventAbout.description}
            artists={pageData.data.artists}
            activePage="exhibition"
          />
        )}
        {checkStickyNavItem(
          pageData.data.stickyNavItems,
          'selectionsFromWorks'
        ) && (
          <SelectionsFromWork
            stickyId={getStickyId(
              pageData.data.stickyNavItems,
              'selectionsFromWorks'
            )}
            sectionTitle={t('exhibition-and-event:selectionsFromWorks.title')}
            sectionSubTitle={t(
              'exhibition-and-event:selectionsFromWorks.subTitle'
            )}
            data={pageData.data.selectionsFromWorks}
          />
        )}
        {checkStickyNavItem(
          pageData.data.stickyNavItems,
          'photosAndVideos'
        ) && (
          <PhotoAndVideoSliderSection
            stickyId={getStickyId(
              pageData.data.stickyNavItems,
              'photosAndVideos'
            )}
            data={pageData.data.photosAndVideos}
            sectionTitle={t('nav:stickyItems.photosAndVideos')}
            minHeight="auto"
            pt={{ base: 'padding.primary', lg: 'padding.primary2' }}
            pb={{ base: 'padding.primary2', lg: 'padding.primary4' }}
            my={{ base: '0', lg: '0' }}
          />
        )}
        {checkStickyNavItem(pageData.data.stickyNavItems, 'threeDTour') && (
          <ThreeDTourEmbedVideo
            stickyId={getStickyId(pageData.data.stickyNavItems, 'threeDTour')}
            embedUrl={pageData.data?.threeDTourEmbedLink}
          />
        )}

        {checkStickyNavItem(pageData.data.stickyNavItems, 'otherExhibitions') ||
        checkStickyNavItem(pageData.data.stickyNavItems, 'relatedEvents') ? (
          <ExhibitionAndEventRelatedEvents
            tabData={pageData.data.relatedEventsAndOtherExhibitions}
            stickyId={getStickyId(
              pageData.data.stickyNavItems,
              pageData.data.relatedEventsAndOtherExhibitions.length === 1
                ? 'otherExhibitions'
                : 'relatedEvents'
            )}
          />
        ) : (
          <Fragment />
        )}
        {checkStickyNavItem(pageData.data.stickyNavItems, 'giftShop') && (
          <SsmShopSliderSection
            stickyId={getStickyId(pageData.data.stickyNavItems, 'giftShop')}
            products={pageData.data.products}
          />
        )}
        <SsmFriendlyCardAdvantages isReverseOnMobile={true} />
      </Box>
    </Layout>
  )
}

export default ExhibitionDetailScreen

export async function getServerSideProps({ query, locale }) {
  const { slug } = query
  const pageData = {
    data: null,
    error: null,
  }

  try {
    const collection = await ExhibitionService.getExhibitionDetailPage(
      slug,
      locale
    )

    pageData.data = collection
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData },
  }
}
